// Career progress across shifts (#13): points earned for safe and effective
// operations accumulate, and unlock the Standard shift (after Lesson 1) and
// more aircraft for the hangar. Each Standard shift's debrief promotes,
// demotes or keeps your skill level, and uses up days of the season. Pure,
// no DOM; saved by services/storage.js.

import { HANGAR, OPS_TYPES, LEVELS, MAX_INCIDENTS } from '../config/constants.js';

export const NEW_CAREER = Object.freeze({
  points: 0, shifts: 0, orientationDone: false,
  level: LEVELS[0].id, // skill level for Standard shifts
  day: 1,              // the season day the next Standard shift starts on
  seasons: 0,          // seasons finished
});

export function isValidCareer(c) {
  return Boolean(c) && Number.isFinite(c.points) && c.points >= 0
    && Number.isInteger(c.shifts) && c.shifts >= 0 && typeof c.orientationDone === 'boolean'
    && LEVELS.some((l) => l.id === c.level)
    && Number.isInteger(c.day) && c.day >= 1 && Number.isInteger(c.seasons) && c.seasons >= 0;
}

/** A career saved before levels and seasons (0.0.3) gets their defaults. */
export function upgradeCareer(c) {
  if (!c || typeof c !== 'object') return null;
  const next = { ...NEW_CAREER, ...c };
  return isValidCareer(next) ? next : null;
}

export const levelIndex = (id) => Math.max(0, LEVELS.findIndex((l) => l.id === id));
export const levelLabel = (id) => LEVELS[levelIndex(id)].label;

/** The season is over once its last day has been worked. */
export function seasonDone(c, seasonDays) {
  return c.day > seasonDays;
}

/** A new season: the day count starts again; level, points and unlocks stay. */
export function startSeason(c) {
  return { ...c, day: 1, seasons: c.seasons + 1 };
}

/**
 * Where a Standard shift's result leaves your level: promote for a finished
 * work order with no incidents and at least the level's promoteScore points;
 * demote when the shift ended on incidents or scored below zero; otherwise
 * stay. Returns { change: 'promote' | 'stay' | 'demote', level, reason }.
 */
export function levelOutcome(levelId, { total, completed, incidents }) {
  const i = levelIndex(levelId);
  const here = LEVELS[i];
  if (incidents >= MAX_INCIDENTS || total < 0) {
    const why = incidents >= MAX_INCIDENTS ? `${MAX_INCIDENTS} incidents ended the shift` : 'the shift scored below zero';
    if (i === 0) return { change: 'stay', level: here.id, reason: `${why}; ${here.label} is the first level` };
    return { change: 'demote', level: LEVELS[i - 1].id, reason: why };
  }
  if (completed && incidents === 0 && here.promoteScore !== null && total >= here.promoteScore) {
    return {
      change: 'promote', level: LEVELS[i + 1].id,
      reason: `work order done with no incidents and ${total} points (${here.promoteScore} needed)`,
    };
  }
  let reason;
  if (here.promoteScore === null) reason = `${here.label} is the top level`;
  else if (!completed) reason = 'finish the work order to be promoted';
  else if (incidents > 0) reason = 'a shift with no incidents is needed for promotion';
  else reason = `${here.promoteScore} points are needed for promotion; this shift scored ${total}`;
  return { change: 'stay', level: here.id, reason };
}

/** Which shift kinds can be started. */
export function shiftUnlocked(c, kind) {
  return kind === 'orientation' || (kind === 'standard' && c.orientationDone);
}

/**
 * The hangar as the player sees it: one row per slot, with a label, whether
 * it's unlocked, and the career points it still needs.
 */
export function hangarFor(c) {
  const counts = {};
  return HANGAR.map((slot) => {
    if (slot.later) return { ...slot, unlocked: false };
    const t = OPS_TYPES.find((x) => x.id === slot.type);
    counts[slot.type] = (counts[slot.type] || 0) + 1;
    const unlocked = c.points >= slot.points;
    return {
      ...slot,
      label: `${t.prefix} ${counts[slot.type]}`,
      typeLabel: t.label,
      unlocked,
      needs: unlocked ? 0 : slot.points - c.points,
      // the first scout and duster always fly: the work order needs both jobs
      required: slot.points === 0,
    };
  });
}

/** Aircraft type ids for a shift from the chosen hangar slot ids (unlocked only, hangar order). */
export function aircraftForShift(c, chosenIds) {
  const chosen = new Set(chosenIds);
  return hangarFor(c)
    .filter((h) => h.unlocked && (h.required || chosen.has(h.id)))
    .map((h) => h.type);
}

/**
 * Add a finished shift to the career. Only positive shift scores add points;
 * finishing Lesson 1's work order unlocks the Standard shift. Returns the new
 * career and what this shift unlocked (labels).
 */
export function recordShift(c, {
  kind, total, completed, incidents = 0, daysUsed = 0,
}) {
  const outcome = kind === 'standard' ? levelOutcome(c.level, { total, completed, incidents }) : null;
  const next = {
    ...c,
    points: c.points + Math.max(0, Math.round(total)),
    shifts: c.shifts + 1,
    orientationDone: c.orientationDone || (kind === 'orientation' && completed),
    level: outcome ? outcome.level : c.level,
    day: c.day + (kind === 'standard' ? daysUsed : 0), // Lesson 1 is training: no season days
  };
  const unlocked = [];
  if (!c.orientationDone && next.orientationDone) unlocked.push('Standard shift');
  const before = new Set(hangarFor(c).filter((h) => h.unlocked).map((h) => h.id));
  for (const h of hangarFor(next)) {
    if (h.unlocked && !before.has(h.id)) unlocked.push(`${h.label} (${h.typeLabel.toLowerCase()})`);
  }
  return { career: next, unlocked, outcome };
}
